import { useAdminLocale } from '../lib/admin-locale.js';

const FALLBACK_COLOR = '#2563EB';
type PreviewStarterIcon = 'QUESTION' | 'INFO' | 'SERVICES' | 'PRICE' | 'CLOCK' | 'CONTACT';

function accent(color: string): string {
  return /^#[0-9A-Fa-f]{6}$/.test(color) ? color : FALLBACK_COLOR;
}

function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  try {
    return new URL(trimmed).toString();
  } catch {
    return null;
  }
}

/** Appearance-only reflection of the saved bot. It does not call the model. */
export function ChatPreview(props: {
  title: string;
  greeting: string;
  color: string;
  position: 'left' | 'right';
  locales: string[];
  logoUrl?: string | null;
  privacyUrl?: string | null;
  contactUrl?: string | null;
  leadNotifyEmail?: string | null;
  starterQuestions?:
    | Array<{
        question: string;
        answer: string;
        icon?: PreviewStarterIcon;
        action?: 'ANSWER' | 'CONTACT' | 'QUOTE';
      }>
    | undefined;
}) {
  const { text } = useAdminLocale();
  const color = accent(props.color);
  const title = props.title.trim() || text('Βοηθός', 'Assistant');
  const privacy = safeHttpUrl(props.privacyUrl);
  const contact = safeHttpUrl(props.contactUrl);
  const notifyEmail = props.leadNotifyEmail?.trim() || null;
  return (
    <div>
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-[#fffdfa] shadow-[0_20px_45px_-24px_rgba(39,39,42,0.35)] dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center gap-2 border-b border-stone-200 px-3 py-2.5 text-slate-800 dark:border-slate-700 dark:text-slate-100">
          {props.logoUrl ? (
            <img
              src={props.logoUrl}
              alt=""
              className="size-7 shrink-0 rounded-lg bg-white object-cover"
            />
          ) : (
            <span
              className="admin-display grid size-7 shrink-0 place-items-center rounded-lg border border-fuchsia-100 bg-gradient-to-br from-rose-50 via-fuchsia-100 to-blue-100 text-xs font-bold"
              style={{ color }}
            >
              {title.slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="admin-display truncate text-xs font-bold">{title}</p>
            <p className="mt-0.5 flex items-center gap-1 text-[9px] text-stone-500 dark:text-slate-400">
              <span className="size-1.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />
              {text('Απαντά άμεσα', 'Answers instantly')}
            </p>
          </div>
          <span className="rounded-lg px-2 py-1 text-[10px] text-stone-500 hover:bg-stone-100 dark:text-slate-400">
            {(props.locales.length > 0 ? props.locales : ['el']).join(' · ')}
          </span>
          <span
            className="grid size-7 place-items-center rounded-lg text-stone-500"
            aria-hidden="true"
          >
            ✉
          </span>
          <span
            className="grid size-7 place-items-center rounded-lg text-lg text-stone-500"
            aria-hidden="true"
          >
            +
          </span>
        </div>
        <div className="border-b border-stone-200 px-3 py-1.5 text-[10px] leading-4 text-stone-500 dark:border-slate-700 dark:text-slate-400">
          <span>
            {text(
              'Συνομιλείτε με αυτοματοποιημένο βοηθό (AI).',
              'You are chatting with an automated assistant (AI).',
            )}
          </span>
          {privacy ? (
            <>
              {' '}
              <a
                href={privacy}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[color:var(--admin-accent)] hover:underline"
              >
                {text('Ενημέρωση απορρήτου', 'Privacy notice')}
              </a>
            </>
          ) : (
            <span className="text-slate-400 dark:text-slate-500">
              {' '}
              · {text('Χωρίς σύνδεσμο απορρήτου', 'No privacy link')}
            </span>
          )}
          {contact ? (
            <>
              {' '}
              <a
                href={contact}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[color:var(--admin-accent)] hover:underline"
              >
                {text('Επικοινωνία', 'Contact')}
              </a>
            </>
          ) : (
            <span className="text-slate-400 dark:text-slate-500">
              {' '}
              · {text('Χωρίς σύνδεσμο επικοινωνίας', 'No contact link')}
            </span>
          )}
        </div>
        <div className="flex min-h-56 flex-col justify-end bg-[#fbfaf8] p-3 dark:bg-slate-950">
          <span className="mb-1 ml-0.5 text-[9px] font-semibold text-stone-500">
            {text('Βοηθός AI', 'AI Agent')}
          </span>
          <p className="max-w-[85%] rounded-xl rounded-bl-sm bg-stone-100 px-3 py-2 text-xs leading-5 text-slate-800 dark:bg-slate-900 dark:text-slate-200">
            {props.greeting.trim() ||
              text(
                'Ο χαιρετισμός της προεπιλεγμένης γλώσσας εμφανίζεται εδώ.',
                'The default-language greeting appears here.',
              )}
          </p>
          {props.starterQuestions && props.starterQuestions.length > 0 ? (
            <div className="mt-3 grid gap-2 pl-3">
              {props.starterQuestions.map((item, index) => (
                <div
                  key={`${item.question}-${index}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="grid size-5 shrink-0 place-items-center rounded-md bg-stone-100 text-[10px]"
                      style={{ color }}
                      aria-hidden="true"
                    >
                      {previewStarterIcon(item.icon ?? 'QUESTION')}
                    </span>
                    <span>{item.question || text('Νέα ερώτηση', 'New question')}</span>
                  </span>
                  <span aria-hidden="true">→</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2 border-t border-stone-200 px-2.5 py-2 dark:border-slate-700">
          <div className="flex-1 rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs text-stone-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500">
            {text('Γράψτε το μήνυμά σας…', 'Type your message…')}
          </div>
          <span
            className="grid size-9 place-items-center rounded-xl text-white"
            style={{ background: color }}
            aria-hidden="true"
          >
            ↑
          </span>
        </div>
        {notifyEmail ? (
          <div className="border-t border-[color:var(--admin-border-soft)] bg-white px-4 py-2 text-[11px] text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
            {text('Επικοινωνήστε μαζί μας στο', 'Contact us at')}{' '}
            <a
              href={`mailto:${notifyEmail}`}
              className="font-medium text-[color:var(--admin-accent)] hover:underline"
            >
              {notifyEmail}
            </a>
          </div>
        ) : (
          <div className="border-t border-[color:var(--admin-border-soft)] bg-white px-4 py-2 text-[11px] text-slate-400 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-500">
            {text(
              'Δεν έχει οριστεί email για ειδοποιήσεις ενδιαφερόντων',
              'No lead notification email has been set',
            )}
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {text(
          'Προεπισκόπηση εμφάνισης · θέση launcher:',
          'Appearance preview · launcher position:',
        )}{' '}
        {props.position === 'left' ? text('αριστερά', 'left') : text('δεξιά', 'right')}
      </p>
    </div>
  );
}

function previewStarterIcon(icon: PreviewStarterIcon): string {
  return {
    QUESTION: '?',
    INFO: 'i',
    SERVICES: '▣',
    PRICE: '€',
    CLOCK: '◷',
    CONTACT: '✉',
  }[icon];
}
